import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, generatePath } from "react-router-dom";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, CircularProgress, Chip, Select, MenuItem, FormControl,
  InputLabel, TextField, Stack, Card, CardContent, useMediaQuery, useTheme,
} from "@mui/material";
import { getAdminOrders } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { useDebounce } from "../../../hooks/useDebounce";
import useListQueryState from "../../../hooks/useListQueryState";

const ORDER_STATUSES = [
  "PENDING", "IN_PROGRESS", "REJECTED", "IN_TRANSIT", "READY_FOR_PICKUP",
  "DELIVERED", "FULFILLED", "CANCELLED", "RETURNED", "REFUNDED", "DISPUTED",
];

const statusColor = {
  PENDING: "default",
  IN_PROGRESS: "info",
  REJECTED: "error",
  IN_TRANSIT: "info",
  READY_FOR_PICKUP: "info",
  DELIVERED: "primary",
  FULFILLED: "success",
  CANCELLED: "default",
  RETURNED: "warning",
  REFUNDED: "warning",
  DISPUTED: "error",
};

// Price negotiation filter values (see listOrdersForAdmin on the backend).
const NEGOTIATION_FILTERS = [
  { value: "any", label: "Any negotiation" },
  { value: "open", label: "Offer open" },
  { value: "agreed", label: "Price agreed" },
  { value: "not_agreed", label: "Not agreed" },
];

const NEGOTIATION_CHIP = {
  PROPOSED: { label: "Offer open", color: "warning" },
  COUNTERED: { label: "Offer open", color: "warning" },
  ACCEPTED: { label: "Price agreed", color: "success" },
  DECLINED: { label: "Not agreed", color: "default" },
  EXPIRED: { label: "Offer expired", color: "default" },
};

const NegotiationChip = ({ order }) => {
  const chip = NEGOTIATION_CHIP[order.priceNegotiation?.status];
  return chip ? (
    <Chip label={chip.label} size="small" color={chip.color} variant="outlined" sx={{ ml: 0.5 }} />
  ) : null;
};

const formatCurrency = (cents, currency) => {
  const amount = ((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const OrderList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, updateParams] = useListQueryState();
  const page = Number(searchParams.get("page")) || 0;
  const statusFilter = searchParams.get("status") || "";
  const sort = searchParams.get("sort") || "newest";
  const negotiationFilter = searchParams.get("negotiation") || "";
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const search = useDebounce(searchInput, 300);
  const limit = 20;

  // See UserList.jsx for why this guards against firing on mount.
  useEffect(() => {
    if (search === (searchParams.get("search") || "")) return;
    updateParams({ search, page: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-orders", page, statusFilter, search, sort, negotiationFilter],
    queryFn: () =>
      getAdminOrders({
        page: page + 1,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
        sort,
        negotiation: negotiationFilter || undefined,
      }),
  });

  const handleRowClick = (orderId) =>
    navigate(generatePath(frontendRoutes.ADMIN_ORDER_DETAIL, { orderId }));

  const orders = data?.orders || [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Orders</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <TextField
          size="small"
          label="Search order #, buyer, or seller"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => updateParams({ status: e.target.value, page: undefined })}
          >
            <MenuItem value="">All</MenuItem>
            {ORDER_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Price negotiation</InputLabel>
          <Select
            value={negotiationFilter}
            label="Price negotiation"
            onChange={(e) => updateParams({ negotiation: e.target.value, page: undefined })}
          >
            <MenuItem value="">All</MenuItem>
            {NEGOTIATION_FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort</InputLabel>
          <Select value={sort} label="Sort" onChange={(e) => updateParams({ sort: e.target.value, page: undefined })}>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
            <MenuItem value="total_desc">Total (high to low)</MenuItem>
            <MenuItem value="total_asc">Total (low to high)</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No orders found.</Typography>
      ) : isMobile ? (
        <Stack spacing={2}>
          {orders.map((order) => (
            <Card key={order._id} variant="outlined" onClick={() => handleRowClick(order._id)} sx={{ cursor: "pointer" }}>
              <CardContent>
                <Typography fontWeight={600}>{order.orderNumber}</Typography>
                <Typography variant="body2">Buyer: {order.user?.name || "—"}</Typography>
                <Typography variant="body2">Shop: {order.shop?.shopName || "—"}</Typography>
                <Typography variant="body2">
                  Status: <Chip label={order.status} size="small" color={statusColor[order.status] || "default"} />
                  <NegotiationChip order={order} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(order.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(order.totalCents, order.currency)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Buyer</TableCell>
              <TableCell>Shop</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order._id} hover onClick={() => handleRowClick(order._id)} sx={{ cursor: "pointer" }}>
                <TableCell>{order.orderNumber}</TableCell>
                <TableCell>{order.user?.name || "—"}</TableCell>
                <TableCell>{order.shop?.shopName || "—"}</TableCell>
                <TableCell>
                  <Chip label={order.status} size="small" color={statusColor[order.status] || "default"} />
                  <NegotiationChip order={order} />
                </TableCell>
                <TableCell>{order.payment?.paymentStatus || "—"}</TableCell>
                <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>{formatCurrency(order.totalCents, order.currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TablePagination
        component="div"
        count={data?.total || 0}
        page={page}
        onPageChange={(_, p) => updateParams({ page: p || undefined })}
        rowsPerPage={limit}
        rowsPerPageOptions={[limit]}
      />
    </Box>
  );
};

export default OrderList;
